import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { type Stakeholder } from '@gat-ship/types'
import { useAppLoading, type ButtonProps } from '@gat-ship/vue-components'
import { useStakeholderPermissions } from '@gat-ship/stakeholders'

/** The button-config shape stored in `stakeholders-text.ts`. */
export interface StakeholderEditButtonConfig {
  label: string
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  icon?: unknown
  class?: string
}

/**
 * Shared logic for a stakeholder detail edit button — loading spinner (keyed
 * on the stakeholder id) plus the resolved button config. Visibility delegates
 * to `useStakeholderPermissions().canModify` so the gate lives in one place.
 * Used by BOTH the routed page groups and the detail-modal sections.
 */
export function useStakeholderEditAction(
  stakeholder: MaybeRefOrGetter<Stakeholder | undefined>,
  btnConfig: () => StakeholderEditButtonConfig
) {
  const { isLoading: appIsLoading } = useAppLoading()
  const { canModify } = useStakeholderPermissions()

  const isLoading = computed(() => {
    const value = toValue(stakeholder)
    return value ? appIsLoading(value.id) : false
  })

  const canShow = computed(() => {
    const value = toValue(stakeholder)
    return value ? canModify(value) : false
  })

  return {
    isLoading,
    canShow,
    btnConfig: computed(btnConfig)
  }
}
